import posthog from "posthog-js";
import { Role } from "~/types/enums";
import type { Tables } from "~/types/types.gen.ts";
import { PosthogEvents } from "~/composables/usePosthog";
import { RolePermissions } from "~/utils/permissions";

function getSafeAuthRedirectPath(redirectPath?: string) {
  const normalizedPath = String(redirectPath ?? "").trim();

  if (!normalizedPath) {
    return "";
  }

  if (!normalizedPath.startsWith("/") || normalizedPath.startsWith("//")) {
    return "";
  }

  return normalizedPath;
}

function getAuthRedirectUrl(redirectPath?: string) {
  const safeRedirectPath = getSafeAuthRedirectPath(redirectPath);
  const confirmUrl = new URL(
    "confirm",
    useRuntimeConfig().public.supabaseRedirectBaseUrl,
  );

  if (safeRedirectPath) {
    confirmUrl.searchParams.set("redirect", safeRedirectPath);
  }

  return confirmUrl.toString();
}

export const useUserStore = defineStore("userStore", {
  state: () => ({
    user: null as Tables<"profiles"> | null,
    userSettings: [] as Tables<"user_settings">[],
    isRequestInProgress: false,
    isChangePasswordFlow: false,
    isEmailConfirmFlow: false,
    profileDataFetched: false,
    isAdminUser: false,
    notificationTokens: [] as string[],
  }),
  getters: {
    isUserLoggedIn: () => {
      return useSupabaseSession().value != null;
    },

    isFighter: (state) => {
      return state.user?.role == Role.FIGHTER;
    },

    isGymOwner: (state) => {
      return state.user?.role == Role.GYM_OWNER;
    },

    isOrganizer: (state) => {
      return state.user?.role == Role.ORG_OWNER;
    },

    isReferee: (state) => {
      return state.user?.role == Role.REFEREE;
    },

    isViewer: (state) => {
      return state.user?.role == Role.VIEWER;
    },

    // Only to be used for client checks, sensitive actions should always be verified on the backend.
    isAdmin(state): boolean {
      return state.isAdminUser;
    },

    getSelectedLanguageFromUserProperties: (state) => {
      var selectedLang = "en";
      state.userSettings.forEach((setting) => {
        selectedLang = setting.value!;
      });
      return selectedLang;
    },

    permissions: (state) => {
      return state.user?.role ? RolePermissions[state.user.role] : [];
    },

    usersCurrentEntity: (state) => {
      if (state.user?.role == Role.FIGHTER) {
        return usePlayerStore().firstFighter;
      } else if (state.user?.role == Role.GYM_OWNER) {
        return useGymStore().gymsForUser[0];
      } else if (state.user?.role == Role.ORG_OWNER) {
        return useOrganisationStore().usersOrganisations[0];
      }
      return null;
    },

    canUserProceedToHomepage: (state) => {
      const userFinishedOnboarding = state.user?.onboarding_finished;

      if (state.user?.role === Role.VIEWER) {
        return true;
      }

      const isOrganizerRole = state.user?.role == Role.ORG_OWNER;
      const organisation = useOrganisationStore().usersOrganisations[0];

      if (isOrganizerRole && organisation) {
        return userFinishedOnboarding && organisation.verified;
      }

      return userFinishedOnboarding;
    },

    canAccessAppNavigation: (state) => {
      if (!state.user) {
        return false;
      }

      if (state.user.role === Role.VIEWER) {
        return true;
      }

      if (state.user.onboarding_finished) {
        return true;
      }

      const properties = (state.user.properties ?? {}) as Record<
        string,
        unknown
      >;
      const committedEntity = properties.onboarding_committed_entity as
        | { role?: Role }
        | undefined;

      return (
        committedEntity?.role === Role.FIGHTER ||
        committedEntity?.role === Role.GYM_OWNER
      );
    },

    hasFinishedOnboarding: (state) => {
      return state.user?.onboarding_finished ?? false;
    }
  },
  actions: {
    async signUp(email: string, password: string, redirectPath?: string) {
      this.isRequestInProgress = true;

      const { error } = await useSupabaseClient().auth.signUp({
        email: email,
        password: password,
        options: {
          emailRedirectTo: getAuthRedirectUrl(redirectPath),
        },
      });

      if (error) {
        usePosthog().posthogLogError({
          message: "Sign up error",
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "user.ts:signUp",
          },
        });

        throw error;
      }
    },

    async login(email: string, password: string) {
      const { data, error } = await useSupabaseClient().auth.signInWithPassword(
        {
          email: email,
          password: password,
        },
      );

      if (error) {
        usePosthog().posthogLogError({
          message: "Login error",
          error,
          context: {
            userId: useUserStore().user?.id,
            action: "user.ts:login",
          },
        });

        throw error;
      }

      await this.fetchProfileData();

      posthog.identify(this.user?.id, {
        email: this.user?.email,
        role: this.user?.role,
        environment: useRuntimeConfig().public.environment,
      });

      posthog.capture(PosthogEvents.LOG_IN);
    },

    async logout(): Promise<boolean> {
      const { error } = await useSupabaseClient().auth.signOut();

      const success = error == null;

      if (success) {
        // clear local and session storage
        [window.localStorage, window.sessionStorage].forEach((storage) => {
          Object.entries(storage).forEach(([key]) => {
            if (
              ![
                "rememberMeEmail",
                "rememberMeCheckbox",
                "returningUser",
                "cookie-consent",
              ].includes(key)
            ) {
              storage.removeItem(key);
            }
          });
        });

        this.user = null;

        posthog.capture(PosthogEvents.LOG_OUT);

        usePlayerStore().$reset();
        useGymStore().$reset();
        useOrganisationStore().$reset();
        useMessagesStore().$reset();
        useNotificationsStore().reset();
        useSearchStore().$reset();
        this.$reset();
        posthog.reset();
      } else {
        console.log("ERROR: " + error?.message);
        displayErrorNotification();
      }

      return success;
    },

    async fetchProfileData() {
      if (this.profileDataFetched || !this.isUserLoggedIn || this.user != null) {
        return;
      }

      try {
        // Use useFetch for SSR support - it will fetch on server and hydrate on client
        const { data, error } = await useFetch("/api/profile/data", {
          key: "user-profile-data",
        });

        if (error.value) {
          throw error.value;
        }

        if (data.value) {
          this.user = data.value.profile;
          this.isAdminUser = Boolean(data.value.is_admin);
          this.userSettings = data.value.settings;
          this.notificationTokens = data.value.notificationTokens;

          this.profileDataFetched = true;
        }
      } catch (error) {
        console.error("Failed to fetch profile data:", error);

        // Only log error if we're on the client side and can access posthog
        if (process.client) {
          usePosthog().posthogLogError({
            message: "Failed to fetch profile data",
            error,
            context: {
              userId: this.user?.id,
              action: "user.ts:fetchProfileData",
            },
          });
        }
      }
    },

    async setProfileData(
      userData: Partial<Tables<"profiles">>,
    ): Promise<boolean> {
      const userId = this.user?.id;
      if (userId == null) {
        displayErrorNotification();
        return false;
      }

      const { data, error } = await useSupabaseClient()
        .from("profiles")
        .upsert({
          id: userId,
          created_at: userData.created_at,
          updated_at: userData.updated_at,
          username: null,
          full_name: userData.full_name,
          avatar_url: userData.avatar_url,
          file_id_storage: userData.file_id_storage,
          email: userData.email,
          role: userData.role,
          onboarding_finished: userData.onboarding_finished,
          properties: userData.properties,
        })
        .eq("id", userId)
        .select()
        .single();

      if (error == null && data != null) {
        this.user = data;
        return true;
      } else {
        displayErrorNotification();
        console.log("ERROR: " + error.message);
        return false;
      }
    },

    async updateUserPassword(
      newPassword: string,
      currentPassword?: string,
    ): Promise<{ success: boolean; errorMessageKey?: string }> {
      try {
        await usePrivateApiFetch()<{
          success: boolean;
        }>("/api/profile/change-password", {
          method: "POST",
          body: {
            currentPassword,
            newPassword,
          },
        });

        return { success: true };
      } catch (error: any) {
        const statusMessage =
          error?.data?.statusMessage ?? error?.statusMessage;
        let errorMessage = "common.error";

        if (statusMessage === "CURRENT_PASSWORD_INVALID") {
          errorMessage =
            "profile.change_password_form.error.invalid_current_password";
        } else if (statusMessage === "SAME_PASSWORD") {
          errorMessage = "profile.change_password_form.error.same_password";
        }

        return {
          success: false,
          errorMessageKey: errorMessage,
        };
      }
    },

    async updateUserSetting(key: string, value: string): Promise<boolean> {
      const setting = this.userSettings.filter((setting) => {
        return setting.key == key;
      });

      var resultData;
      var resultError;

      if (setting.length > 0) {
        const { data, error } = await useSupabaseClient()
          .from("user_settings")
          .update({
            updated_at: new Date().toJSON(),
            key: key,
            value: value,
          })
          .eq("id", setting[0].id)
          .select();

        resultData = data;
        resultError = error;
      } else {
        const { data, error } = await useSupabaseClient()
          .from("user_settings")
          .insert({
            user_id: this.user?.id,
            updated_at: new Date().toJSON(),
            key: key,
            value: value,
          })
          .select();

        resultData = data;
        resultError = error;
      }

      if (resultError == null && resultData != null) {
        if (setting.length > 0) {
          this.userSettings.forEach((setting) => {
            if (setting.key == key) {
              setting.value = value;
            }
          });
        } else {
          this.userSettings.push(resultData[0]);
        }

        return true;
      } else {
        displayErrorNotification();
        console.log("ERROR: " + resultError?.message);
        return false;
      }
    },

    getUserSettingValue(key: string): string | null {
      return (
        this.userSettings.find((setting) => setting.key === key)?.value ?? null
      );
    },

    hasUserSettingValue(key: string, value: string): boolean {
      return this.getUserSettingValue(key) === value;
    },

    async getProfileDataForId(
      id: string | null,
    ): Promise<Tables<"profiles"> | undefined> {
      if (id == null) {
        return undefined;
      }

      const { data } = await useSupabaseClient()
        .from("profiles")
        .select()
        .eq("id", id);

      if (data != null) {
        return data[0];
      }

      return undefined;
    },

    async triggerPasswordReset(
      email: string,
    ): Promise<{ success: boolean; errorMessage: string | undefined }> {
      const config = useRuntimeConfig();

      const { data, error } =
        await useSupabaseClient().auth.resetPasswordForEmail(email, {
          redirectTo: `${config.public.supabaseRedirectBaseUrl}`,
        });

      const success = error == null;

      var errorMessage: string | undefined;

      if (!success) {
        if (error?.code == "over_email_send_rate_limit") {
          errorMessage =
            "login.modal.forgot_password.error.over_email_send_rate_limit";
        } else {
          errorMessage = "common.error";
        }
      }

      return {
        success: success,
        errorMessage: errorMessage,
      };
    },

    // Deletes the account on the server (which also removes stored files), then signs out.
    // transfers maps each owned gym/organisation id to the member who takes it over.
    // Records that the user is at least 16, which having a profile requires.
    async confirmAge() {
      if (this.user?.age_confirmed_at) {
        return;
      }

      const { age_confirmed_at } = await usePrivateApiFetch()<{
        age_confirmed_at: string;
      }>("/api/profile/age-confirmation", { method: "POST" });

      if (this.user) {
        this.user.age_confirmed_at = age_confirmed_at;
      }
    },

    // For a user under 16: keeps the account as a viewer and lets go of their profiles.
    async stepBackToViewer(transfers: Record<string, string>) {
      await usePrivateApiFetch()("/api/profile/step-back", {
        method: HttpMethod.POST,
        body: { transfers },
      });

      // The role and owned profiles changed, so reload instead of patching stores.
      await reloadNuxtApp({ path: "/" });
    },

    async deleteAccount(
      confirmation: string,
      transfers: Record<string, string>,
    ): Promise<void> {
      await $fetch("/api/account/delete", {
        method: "POST",
        body: { confirmation, transfers },
      });

      await this.logout();
    },

    async verifyAdminAccess(): Promise<boolean> {
      try {
        const response = await usePrivateApiFetch()<{
          is_admin: boolean;
        }>("/api/admin/access");

        this.isAdminUser = Boolean(response.is_admin);

        return this.isAdminUser;
      } catch {
        this.isAdminUser = false;
        return false;
      }
    },
  },
});
